import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { KeyRound, Eye, EyeOff } from 'lucide-react';
import { useT } from '@/store/i18n';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { api } from '@/lib/api';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ChangePasswordModal({ open, onClose }: Props) {
  const t = useT();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  function reset() {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowPwd(false);
  }

  const submit = useMutation({
    mutationFn: () =>
      api('users/change-password.php', {
        method: 'POST',
        json: {
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        },
      }),
    onSuccess: () => {
      toast.success(t('password.success'));
      reset();
      onClose();
    },
    onError: (e: Error) => toast.error(t('password.fail'), e.message),
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.warning(t('password.val.short'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.warning(t('password.val.mismatch'));
      return;
    }
    submit.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title={t('password.title')}
      description={t('password.desc')}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }}>{t('btn.cancel')}</Button>
          <Button
            type="submit"
            form="change-pwd-form"
            loading={submit.isPending}
            leftIcon={<KeyRound className="w-4 h-4" />}
          >
            {t('password.save')}
          </Button>
        </>
      }
    >
      <form id="change-pwd-form" onSubmit={onSubmit} className="space-y-4">
        <Input
          label={t('password.current')}
          type={showPwd ? 'text' : 'password'}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
          autoFocus
          autoComplete="current-password"
          rightIcon={
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              className="pointer-events-auto text-slate-400 hover:text-slate-700"
              aria-label={showPwd ? t('password.hide') : t('password.show')}
            >
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          }
        />
        <Input
          label={t('password.new')}
          type={showPwd ? 'text' : 'password'}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          hint={t('password.hint')}
          required
          autoComplete="new-password"
        />
        <Input
          label={t('password.confirm')}
          type={showPwd ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          autoComplete="new-password"
        />
      </form>
    </Modal>
  );
}
